import { Button } from 'foundations-components/transitional/components/Button';
import { Popover } from 'foundations-components/transitional/components/Popover';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo } from 'react';
import { PIPELINE_WITH_PERMISSIONS } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { useFetchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { isLoading } from 'reference-resolvers-lite/Status';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { useHttpClient } from '../../../client/HttpClientContext';
import { SkeletonText } from '../../../loaders/SkeletonText';
import { getDataAttributes } from '../../../utils/getDataAttributes';
import { getTransferrableProps } from '../../../utils/getTransferrableProps';
import { RESOLVER_DEBOUNCE_KEY, TIME_TOKENS } from '../../../utils/timeTokens';
import { useAdditionalPropertyValues } from '../../../v2/hooks/useAdditionalPropertyValues';
import { useObjectTypeId } from '../../../v2/hooks/useObjectTypeId';
import { usePropertyValue } from '../../../v2/hooks/usePropertyValue';
import { useStageChangeValidation } from '../hooks/useStageChangeValidation';
import { getPipelinePropertyName, getPipelineStagePropertyName } from '../utils/pipelineUtils';
import PipelinePopoverBody from './PipelinePopoverBody';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PipelineButton = props => {
  const {
    autoFocus,
    readOnly,
    disabled,
    onChangeWithStageValidation,
    popoverOpen,
    setPopoverOpen,
    popoverProps,
    use = 'form',
    truncate = false,
    formControlUse
  } = props;
  const objectTypeId = useObjectTypeId();
  const httpClient = useHttpClient();
  const value = usePropertyValue();
  const pipelineResolver = createStandardCachedResolver(PIPELINE_WITH_PERMISSIONS, objectTypeId, {
    httpClient,
    debouncePeriod: TIME_TOKENS[RESOLVER_DEBOUNCE_KEY]
  });
  const {
    status,
    reference
  } = useFetchResolver(pipelineResolver, value === null ? undefined : value);
  const buttonDisplayText = useMemo(() => {
    if (isLoading(status)) {
      return /*#__PURE__*/_jsx(SkeletonText, {
        "data-test-id": "loading-text"
      });
    }
    return (reference === null || reference === void 0 ? void 0 : reference.label) || value || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataProperties.PropertyInputPipelineV2.placeholder"
    });
  }, [status, reference, value]);
  const {
    data: additionalValues,
    loading: additionalValuesLoading
  } = useAdditionalPropertyValues({
    pipelineStageId: getPipelineStagePropertyName(objectTypeId)
  });
  if (additionalValuesLoading || additionalValues === undefined) {
    return null;
  }
  const {
    pipelineStageId
  } = additionalValues;
  const defaultPipelineId = value === undefined ? null : value;
  const defaultPipelineStageId = pipelineStageId === undefined ? null : pipelineStageId;
  const handleConfirm = ({
    pipelineId: newPipelineId,
    pipelineStageId: newPipelineStageId,
    stageReference
  }) => {
    setPopoverOpen(false);
    if (newPipelineId === defaultPipelineId && newPipelineStageId === defaultPipelineStageId) {
      return;
    }
    const changes = {
      [getPipelinePropertyName(objectTypeId)]: newPipelineId,
      [getPipelineStagePropertyName(objectTypeId)]: newPipelineStageId
    };
    onChangeWithStageValidation(changes, stageReference);
  };
  const useWithTableCellOverride = formControlUse === 'table-cell' ? 'unstyled' : use;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(Popover, Object.assign({
      placement: "right",
      inlineSize: "450px"
    }, popoverProps, {
      open: popoverOpen,
      closeOnOutsideClick: true,
      onOpenChange: e => {
        setPopoverOpen(e.target.value);
      },
      content: /*#__PURE__*/_jsx(PipelinePopoverBody, Object.assign({
        onConfirm: handleConfirm,
        onCancel: () => setPopoverOpen(false),
        defaultPipelineId: defaultPipelineId,
        defaultPipelineStageId: defaultPipelineStageId
      }, props)),
      children: /*#__PURE__*/_jsx(Button, Object.assign({}, getDataAttributes(props), getTransferrableProps(props, ['onChange']), {
        autoFocus: autoFocus,
        disabled: readOnly || disabled,
        onClick: () => setPopoverOpen(true),
        use: useWithTableCellOverride,
        truncate: truncate,
        children: buttonDisplayText
      }))
    }))
  });
};
PipelineButton.displayName = 'PipelineButton';
const PipelineButtonWrapper = props => {
  const {
    onChangeMultiple,
    inputMode
  } = props;
  const {
    stageChangeValidatorLoading,
    stageValidationModal,
    onChangeWithStageValidation
  } = useStageChangeValidation({
    onChangeMultiple,
    inputMode
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PipelineButton, Object.assign({}, props, {
      onChangeWithStageValidation: onChangeWithStageValidation,
      stageChangeValidatorLoading: stageChangeValidatorLoading
    })), stageValidationModal]
  });
};
export default PipelineButtonWrapper;